import _ from 'lodash';
import { connect } from 'react-redux';
import { FixedSizeList } from 'react-window';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { makeStyles } from '@mui/styles';
import { Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
/** import mui components in the section above, Sort Lines Ascending */

import { BlockLoadGroup } from 'widgets-v5/load-group';
import { SearchInput } from 'widgets-v6/standard-input';
import ClickToCopyButton from 'widgets/click-to-copy-button';
/** import widgets components in the section above, Sort Lines Ascending */

import { ADDICTIVE_ORG, isInternalUser } from 'states/profile/business-rules';
/** import multi application imports in the section above, Sort Lines Ascending */

import { can } from 'services/can';
import { default as adminOrgFormActions } from 'packages/forms/admin-org-form/actions';
import { default as adminUserFormActions } from 'packages/forms/admin-users-form/actions';
import { default as deleteModalActions } from 'packages/containers/delete-user-modal/actions';
import { default as deprecateOrgModalActions } from 'packages/containers/deprecate-org-modal/actions';
import { formatNumber_whole } from 'utils/formatting';
import { PermissionsMapping } from 'common/constants/role-intents';
import { rolePresentationDictionary } from 'common/constants/role-presentation-dict';
import actions from './actions';
import AdminOrgForm from 'packages/forms/admin-org-form';
import AdminUserForm from 'packages/forms/admin-users-form';
import DeleteUserModal from 'packages/containers/delete-user-modal';
import DeprecateOrgModal from 'packages/containers/deprecate-org-modal';
import selector from './selector';
/** import single application imports in the section above, Sort Lines Ascending */

const dashboardStyles = makeStyles(theme => ({
    marginBottom: {
        marginBottom: theme.spacing(2),
    },
    panelPaper: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    panelContent: {
        padding: theme.spacing(1.5),
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& th:first-of-type, & td:first-of-type': {
            paddingLeft: 0,
        },
    },
    pageRoot: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
    },
    // Outer wrapper owns flex chain; descendant selectors constrain BlockLoadGroup + LoadGroup
    pageLoadGroup: {
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& .ef4-block-load-group': {
            flex: 1,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
        '& .ef4-load-group': {
            flex: 1,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
    },
    name: {
        marginRight: theme.spacing(0.5),
    },
    sidebarSection: {
        marginBottom: theme.spacing(1.5),
    },
    gridRoot: {
        flex: 1,
        minHeight: 0,
        height: '100%',
    },
    gridItem: {
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    sidebarBody: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: theme.spacing(1.5),
    },
    orgListScroll: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    orgListVirtualHost: {
        flex: 1,
        minHeight: 0,
    },
    // Matches impersonate ef4-navigation__item hover (@ef5-color__background_neutral-hover).
    orgList: {
        '& .MuiListItem-root:hover': {
            backgroundColor: '#F5F5F5',
        },
    },
    // ListItem uses disablePadding — padding lives on orgListItemInner so MUI defaults cannot drift.
    orgListItem: {
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        height: '100%',
        width: '100%',
    },
    // Shared content inset for All + virtualized org rows (label left / People icon right).
    orgListItemInner: {
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        minWidth: 0,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    tableScroll: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    usersTable: {
        width: '100%',
        // Matches impersonate ef4-data-table__data-row:hover.
        '& tbody .MuiTableRow-root:hover': {
            backgroundColor: '#F5F5F5',
        },
        '& th, & td': {
            wordBreak: 'break-word',
        },
        '& th:nth-child(3), & td:nth-child(3)': {
            whiteSpace: 'nowrap',
            wordBreak: 'normal',
        },
        '& th:last-child, & td:last-child': {
            width: 120,
            minWidth: 120,
            maxWidth: 120,
            whiteSpace: 'nowrap',
            wordBreak: 'normal',
            boxSizing: 'border-box',
        },
    },
    searchContainer: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexShrink: 0,
    },
    button: {
        height: '42px',
    },
    orgListItemActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.25),
        flexShrink: 0,
    },
    orgListItemText: {
        flex: 1,
        minWidth: 0,
        // Match own-org users TableCell body (theme MuiTableCell.body + body2 metrics).
        '& .MuiListItemText-primary': {
            fontFamily: theme.typography.fontFamily,
            fontSize: 12.5,
            fontWeight: theme.typography.fontWeightRegular,
            lineHeight: theme.typography.body2.lineHeight,
            letterSpacing: theme.typography.body2.letterSpacing,
        },
    },
    buttonLabel: {
        fontSize: '11px',
    },
}));

const organizationTypes = [
    {
        label: 'Advertisers',
        value: 'advertiser',
    },
    {
        label: 'Partners',
        value: 'partner',
    },
    {
        label: 'Sales Reps',
        value: 'sales_rep',
    },
    {
        label: 'Media Buyers',
        value: 'media_buyer',
    },
    {
        label: 'Admins',
        value: 'admin',
    },
];

const orgTypeLabelMap = {
    admin: 'Admin',
    media_buyer: 'Media Buyer',
    advertiser: 'Advertiser',
    sales_rep: 'Sales Rep',
    partner: 'Partner',
};

const ORG_LIST_ITEM_HEIGHT = 40;

/**
 * Lightweight debounce for search inputs — avoids lodash in this module.
 *
 * @param {(...args: unknown[]) => void} fn
 * @param {number} delayMs
 * @returns {((...args: unknown[]) => void) & { cancel: () => void }}
 */
function createDebounced(fn, delayMs) {
    let timeoutId = null;
    const debounced = (...args) => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            timeoutId = null;
            fn(...args);
        }, delayMs);
    };
    debounced.cancel = () => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };
    return debounced;
}

/**
 * Measures an element's height via ResizeObserver. Re-attaches when `ready` becomes true
 * (host may mount only after loading / list content appears).
 *
 * @param {React.RefObject<HTMLElement|null>} ref
 * @param {boolean} ready - When false, height stays 0 and observer is not attached
 * @returns {number}
 */
function useObservedHeight(ref, ready) {
    const [height, setHeight] = useState(0);

    useLayoutEffect(() => {
        if (!ready) {
            setHeight(0);
            return undefined;
        }

        const el = ref.current;
        if (!el) {
            return undefined;
        }

        const update = () => {
            const next = Math.round(el.getBoundingClientRect().height);
            setHeight(prev => (prev !== next ? next : prev));
        };

        const resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(update);
        });

        resizeObserver.observe(el);
        update();

        return () => {
            resizeObserver.disconnect();
        };
    }, [ref, ready]);

    return height;
}

const ChildOrganizationDashboard = ({
    dispatch,
    isPageLoading,
    currentOrganization,
    filteredUsers,
    filteredOrganizationsList,
    organizationsForList,
    ownOrganizationType,
    isTableLoading,
    isSideBarLoading,
    ownOrganizationId,
    currentUserId,
    users,
}) => {
    const classes = dashboardStyles();
    const [currentTab, setCurrentTab] = useState('advertiser');
    const [organizationSearchKeywords, setOrganizationSearchKeywords] = useState('');
    const orgVirtualListHostRef = useRef(null);
    const orgListOuterRef = useRef(null);
    // All row sits outside FixedSizeList — match the list's clientWidth so label/icons share
    // the same content box as virtualized rows (scrollbar would otherwise only inset the list).
    const [orgListContentWidth, setOrgListContentWidth] = useState(null);
    // Dispatch init before paint to avoid a flash of empty org/user lists on SPA navigation
    useLayoutEffect(() => {
        dispatch(actions.init());
    }, []);

    const onChangeUserTextFilterDebounced = useMemo(
        () => createDebounced(value => dispatch(actions.filterUsers(value)), 300),
        []
    );

    const onChangeOrganizationTextFilterDebounced = useMemo(
        () => createDebounced(value => dispatch(actions.filterOrganizationsByText(value)), 300),
        []
    );

    useEffect(() => {
        return () => {
            onChangeUserTextFilterDebounced.cancel();
            onChangeOrganizationTextFilterDebounced.cancel();
        };
    }, [onChangeUserTextFilterDebounced, onChangeOrganizationTextFilterDebounced]);

    const onChangeUserTextFilter = e => {
        const { value } = e.target;
        onChangeUserTextFilterDebounced(value);
    };
    const onChangeOrganizationTextFilter = e => {
        const { value } = e.target;
        setOrganizationSearchKeywords(value);
        onChangeOrganizationTextFilterDebounced(value);
    };
    const onChangeOrganizationTypeFilter = (event, value) => {
        setCurrentTab(value);
        setOrganizationSearchKeywords('');
        onChangeOrganizationTextFilterDebounced.cancel();
        dispatch(actions.changeOrgFilterType(value));
    };
    /**
     * Sidebar selection from bulk-loaded cache (no network).
     * @param {string|'all'} value - Org id or 'all'
     */
    const selectUsersView = value => {
        if (value === 'all') {
            return dispatch(actions.selectAllOrganizations());
        }
        return dispatch(actions.selectOrganization(value));
    };

    /**
     * Prefer cache-patch after user CRUD when AdminUserForm / delete modal pass a user
     * (or `{ id, organization, _deleted: true }`). Falls back to GraphQL only when no id.
     *
     * @param {Object} [savedUser] - API user or delete marker from deleteModalActions
     */
    const refreshUsersFromServer = savedUser => {
        if (savedUser?.id) {
            return dispatch(actions.applySavedUser(savedUser));
        }
        if (currentOrganization === 'all') {
            return dispatch(actions.fetchAllOrganizationsByType(currentTab));
        }
        return dispatch(actions.fetchOrganizationData(currentOrganization.id));
    };

    /**
     * After create/edit on this page: clear search, upsert org into sidebar, select it.
     * New orgs get an empty user list; existing org edits reuse cached users (no refetch).
     * @param {Object} org - Saved organization from AdminOrgForm
     */
    const refreshOrganization = async org => {
        if (!org || !org.id) {
            return;
        }

        // Check before upsertChildOrganization mutates organizationsForList
        const isNewOrg = !organizationsForList.some(existingOrg => existingOrg.id === org.id);

        setOrganizationSearchKeywords('');
        onChangeOrganizationTextFilterDebounced.cancel();
        dispatch(actions.filterOrganizationsByText(''));
        dispatch(actions.upsertChildOrganization(org));

        if (isNewOrg) {
            dispatch(actions.applySavedOrganization(org, []));
            return;
        }

        const users = await dispatch(actions.resolveUsersAfterOrgSave(org.id));
        dispatch(actions.applySavedOrganization(org, users));
    };

    // Combined network flags drive one page-level BlockLoadGroup (sync search does not flip these)
    const isPageLoadingUi = isPageLoading || isSideBarLoading || isTableLoading;

    let tabs = _.cloneDeep(organizationTypes);

    if (ownOrganizationType !== 'admin') {
        tabs = _.filter(tabs, tab => tab.value !== 'admin' && tab.value !== 'media_buyer');
    }

    if (!isInternalUser()) {
        tabs = _.filter(
            tabs,
            tab => tab.value !== 'admin' && tab.value !== 'partner' && tab.value !== 'sales_rep'
        );
    }

    if (ownOrganizationId === ADDICTIVE_ORG) {
        tabs = _.filter(tabs, tab => tab.value !== 'media_buyer');
    }

    const currentTabLabel = _.get(_.find(tabs, tab => tab.value === currentTab), 'label');
    // During initial load currentOrganization is null but bulk fetch selects 'all' when it completes
    const isAllOrgsView =
        currentOrganization === 'all' || (currentOrganization === null && isPageLoadingUi);
    const orgListReady = !isPageLoadingUi && filteredOrganizationsList.length > 0;
    const orgVirtualListHeight = useObservedHeight(orgVirtualListHostRef, orgListReady);

    useLayoutEffect(() => {
        if (!orgListReady || orgVirtualListHeight <= 0) {
            setOrgListContentWidth(null);
            return undefined;
        }

        const el = orgListOuterRef.current;
        if (!el) {
            return undefined;
        }

        const update = () => {
            const next = el.clientWidth;
            setOrgListContentWidth(prev => (prev !== next ? next : prev));
        };

        const resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(update);
        });

        resizeObserver.observe(el);
        update();

        return () => {
            resizeObserver.disconnect();
        };
    }, [orgListReady, orgVirtualListHeight, filteredOrganizationsList.length]);

    return (
        <div className={classes.pageRoot}>
            <div className={classes.pageLoadGroup}>
                <BlockLoadGroup size="large" isLoading={isPageLoadingUi}>
                    <Grid container spacing={1} className={classes.gridRoot}>
                        {/* admin_subOrg__view — urban GraphQL organizations @authorize(admin_subOrg__view); matches tab + list query. */}
                        {!isPageLoadingUi && can(PermissionsMapping.ADMIN_SUB_ORG__VIEW) && (
                            <Grid item xs={4} className={classes.gridItem}>
                                <Paper className={classes.panelPaper}>
                                    <div className={classes.marginBottom}>
                                        <Tabs
                                            value={currentTab}
                                            onChange={onChangeOrganizationTypeFilter}
                                            indicatorColor="primary"
                                            textColor="primary"
                                            variant="scrollable"
                                            scrollButtons="auto"
                                        >
                                            {_.map(tabs, tab => (
                                                <Tab
                                                    key={tab.value}
                                                    label={tab.label}
                                                    value={tab.value}
                                                />
                                            ))}
                                        </Tabs>
                                    </div>
                                    <div className={classes.sidebarBody}>
                                        <div className={classes.searchContainer}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={8}>
                                                    <SearchInput
                                                        key={currentTab}
                                                        value={organizationSearchKeywords}
                                                        // filterRecordsByKeywords supports name and org id for all users; the id hint
                                                        // in the placeholder is internal-only (not relevant for non-internal users).
                                                        // Keep isInternalUser() here in case section visibility changes.
                                                        placeholder={
                                                            isInternalUser()
                                                                ? `Search ${currentTabLabel} by Name or ID`
                                                                : `Search ${currentTabLabel} by Name`
                                                        }
                                                        onChange={onChangeOrganizationTextFilter}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    {/* admin_subOrg__edit — urban POST /organizations (subOrgEditMw); sub-org create only. */}
                                                    {can(
                                                        PermissionsMapping.ADMIN_SUB_ORG__EDIT
                                                    ) && (
                                                        <Button
                                                            className={classes.button}
                                                            classes={{ label: classes.buttonLabel }}
                                                            color="primary"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            onClick={() =>
                                                                dispatch(
                                                                    adminOrgFormActions.openForm(
                                                                        null,
                                                                        currentTab
                                                                    )
                                                                )
                                                            }
                                                        >
                                                            {`New ${orgTypeLabelMap[currentTab]}`}
                                                        </Button>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </div>
                                        <Box className={classes.orgListScroll}>
                                            {/* Suppress empty state while bulk fetch or tab change is in flight */}
                                            {!isPageLoadingUi &&
                                            filteredOrganizationsList.length === 0 ? (
                                                <Typography align="center">{`No ${currentTabLabel} Found`}</Typography>
                                            ) : (
                                                filteredOrganizationsList.length > 0 && (
                                                    <>
                                                        {/* "All …" stays outside FixedSizeList — width tracks list clientWidth so it aligns with scrolled rows. */}
                                                        {/* admin_user__view — UI gate; urban GET /users (viewUsersMw); GraphQL nested users is looser. */}
                                                        {can(
                                                            PermissionsMapping.ADMIN_USER__VIEW
                                                        ) && (
                                                            <div
                                                                className={classes.orgList}
                                                                style={{
                                                                    flexShrink: 0,
                                                                    height: ORG_LIST_ITEM_HEIGHT,
                                                                    width:
                                                                        orgListContentWidth !== null
                                                                            ? orgListContentWidth
                                                                            : '100%',
                                                                    boxSizing: 'border-box',
                                                                }}
                                                            >
                                                                <ListItem
                                                                    disablePadding
                                                                    className={classes.orgListItem}
                                                                    selected={
                                                                        currentOrganization ===
                                                                        'all'
                                                                    }
                                                                >
                                                                    <div
                                                                        className={
                                                                            classes.orgListItemInner
                                                                        }
                                                                    >
                                                                        <ListItemText
                                                                            className={
                                                                                classes.orgListItemText
                                                                            }
                                                                        >{`All ${currentTabLabel} (${formatNumber_whole(
                                                                            organizationsForList.length
                                                                        )} ${
                                                                            organizationsForList.length ===
                                                                            1
                                                                                ? 'organization'
                                                                                : 'organizations'
                                                                        })`}</ListItemText>
                                                                        <Box
                                                                            className={
                                                                                classes.orgListItemActions
                                                                            }
                                                                        >
                                                                            <Tooltip
                                                                                title={
                                                                                    <Typography variant="body1">
                                                                                        Manage all
                                                                                        users
                                                                                    </Typography>
                                                                                }
                                                                            >
                                                                                <IconButton
                                                                                    size="small"
                                                                                    aria-label="Manage all users"
                                                                                    onClick={() =>
                                                                                        selectUsersView(
                                                                                            'all'
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <PeopleOutlinedIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Box>
                                                                    </div>
                                                                </ListItem>
                                                            </div>
                                                        )}
                                                        <Box
                                                            ref={orgVirtualListHostRef}
                                                            className={classes.orgListVirtualHost}
                                                        >
                                                            {orgVirtualListHeight > 0 && (
                                                                // FixedSizeList virtualizes long org sidebars without mounting every ListItem.
                                                                // overflowY scroll keeps a stable clientWidth so the All row can match it.
                                                                <FixedSizeList
                                                                    className={classes.orgList}
                                                                    outerRef={orgListOuterRef}
                                                                    height={orgVirtualListHeight}
                                                                    itemCount={
                                                                        filteredOrganizationsList.length
                                                                    }
                                                                    itemSize={ORG_LIST_ITEM_HEIGHT}
                                                                    width="100%"
                                                                    overscanCount={5}
                                                                    style={{ overflowY: 'scroll' }}
                                                                >
                                                                    {({ index, style }) => {
                                                                        const org =
                                                                            filteredOrganizationsList[
                                                                                index
                                                                            ];
                                                                        // EFB-1213: issue with Edit/Deprecate when org is not a direct child (urban PATCH PARENT|OWNER only).
                                                                        const isDirectChild =
                                                                            org
                                                                                .relationshipToAuthOrganization
                                                                                ?._relationship ===
                                                                            'child';

                                                                        return (
                                                                            // FixedSizeList requires position:absolute on the outer row — never override style.position.
                                                                            <div style={style}>
                                                                                <ListItem
                                                                                    key={org.id}
                                                                                    disablePadding
                                                                                    className={
                                                                                        classes.orgListItem
                                                                                    }
                                                                                    selected={
                                                                                        currentOrganization !==
                                                                                            null &&
                                                                                        currentOrganization !==
                                                                                            'all' &&
                                                                                        currentOrganization.id ===
                                                                                            org.id
                                                                                    }
                                                                                >
                                                                                    <div
                                                                                        className={
                                                                                            classes.orgListItemInner
                                                                                        }
                                                                                    >
                                                                                        <ListItemText
                                                                                            className={
                                                                                                classes.orgListItemText
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                org.name
                                                                                            }
                                                                                        </ListItemText>
                                                                                        <Box
                                                                                            className={
                                                                                                classes.orgListItemActions
                                                                                            }
                                                                                        >
                                                                                            {isInternalUser() && (
                                                                                                <ClickToCopyButton
                                                                                                    variant="icon"
                                                                                                    textToCopy={String(
                                                                                                        org.id
                                                                                                    )}
                                                                                                    textToDisplay="Copy organization ID"
                                                                                                />
                                                                                            )}
                                                                                            {/* admin_subOrg__edit — urban PATCH /organizations/:id (subOrgUpdateMw); child-org edit. */}
                                                                                            {/* EFB-1213: issue with Edit/Deprecate when org is not a direct child (urban PATCH PARENT|OWNER only). */}
                                                                                            {can(
                                                                                                PermissionsMapping.ADMIN_SUB_ORG__EDIT
                                                                                            ) && (
                                                                                                <>
                                                                                                    <Tooltip
                                                                                                        title={
                                                                                                            <Typography variant="body1">
                                                                                                                {isDirectChild
                                                                                                                    ? 'Edit organization'
                                                                                                                    : 'You can only edit direct child organizations'}
                                                                                                            </Typography>
                                                                                                        }
                                                                                                    >
                                                                                                        {/* MUI Tooltip does not fire on disabled IconButton — span wrapper required. */}
                                                                                                        <span>
                                                                                                            <IconButton
                                                                                                                size="small"
                                                                                                                aria-label="Edit organization"
                                                                                                                disabled={
                                                                                                                    !isDirectChild
                                                                                                                }
                                                                                                                onClick={() =>
                                                                                                                    dispatch(
                                                                                                                        adminOrgFormActions.openForm(
                                                                                                                            org.id
                                                                                                                        )
                                                                                                                    )
                                                                                                                }
                                                                                                            >
                                                                                                                <EditOutlinedIcon fontSize="small" />
                                                                                                            </IconButton>
                                                                                                        </span>
                                                                                                    </Tooltip>
                                                                                                    <Tooltip
                                                                                                        title={
                                                                                                            <Typography variant="body1">
                                                                                                                {isDirectChild
                                                                                                                    ? 'Deprecate organization'
                                                                                                                    : 'You can only deprecate direct child organizations'}
                                                                                                            </Typography>
                                                                                                        }
                                                                                                    >
                                                                                                        {/* MUI Tooltip does not fire on disabled IconButton — span wrapper required. */}
                                                                                                        <span>
                                                                                                            <IconButton
                                                                                                                size="small"
                                                                                                                aria-label="Deprecate organization"
                                                                                                                disabled={
                                                                                                                    !isDirectChild
                                                                                                                }
                                                                                                                onClick={() =>
                                                                                                                    dispatch(
                                                                                                                        deprecateOrgModalActions.open(
                                                                                                                            org
                                                                                                                        )
                                                                                                                    )
                                                                                                                }
                                                                                                            >
                                                                                                                <DeleteOutlineIcon fontSize="small" />
                                                                                                            </IconButton>
                                                                                                        </span>
                                                                                                    </Tooltip>
                                                                                                </>
                                                                                            )}
                                                                                            {can(
                                                                                                PermissionsMapping.ADMIN_USER__VIEW
                                                                                            ) && (
                                                                                                <Tooltip
                                                                                                    title={
                                                                                                        <Typography variant="body1">
                                                                                                            Manage
                                                                                                            users
                                                                                                        </Typography>
                                                                                                    }
                                                                                                >
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        aria-label="Manage users"
                                                                                                        onClick={() =>
                                                                                                            selectUsersView(
                                                                                                                org.id
                                                                                                            )
                                                                                                        }
                                                                                                    >
                                                                                                        <PeopleOutlinedIcon fontSize="small" />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            )}
                                                                                        </Box>
                                                                                    </div>
                                                                                </ListItem>
                                                                            </div>
                                                                        );
                                                                    }}
                                                                </FixedSizeList>
                                                            )}
                                                        </Box>
                                                    </>
                                                )
                                            )}
                                        </Box>
                                    </div>
                                </Paper>
                            </Grid>
                        )}
                        {/* admin_user__view — UI gate; urban GET /users (viewUsersMw); GraphQL nested users is looser. */}
                        {!isPageLoadingUi && can(PermissionsMapping.ADMIN_USER__VIEW) && (
                            <Grid item xs={8} className={classes.gridItem}>
                                <Grid container className={classes.marginBottom}>
                                    <Grid item xs={6}>
                                        <SearchInput
                                            // User search supports name, id, and email for all users; the id hint in the
                                            // placeholder is internal-only (not relevant for non-internal users).
                                            // Keep isInternalUser() here in case visibility rules change.
                                            placeholder={
                                                isInternalUser()
                                                    ? 'Search User by Name, Email, or ID'
                                                    : 'Search User by Name or Email'
                                            }
                                            onChange={onChangeUserTextFilter}
                                        />
                                    </Grid>
                                </Grid>
                                <Paper className={classes.panelPaper}>
                                    <div className={classes.panelContent}>
                                        <AppBar position="static" color="inherit" elevation={0}>
                                            <Toolbar
                                                disableGutters
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {/* null during initial bulk fetch — treat like "All" until FETCH_END */}
                                                {currentOrganization === null ||
                                                currentOrganization === 'all' ? (
                                                    <Typography variant="h6">
                                                        {`All ${currentTabLabel} (${formatNumber_whole(
                                                            users.length
                                                        )} ${
                                                            users.length === 1 ? 'user' : 'users'
                                                        })`}
                                                    </Typography>
                                                ) : (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <Typography variant="h6" component="span">
                                                            {`${
                                                                currentOrganization.name
                                                            } (${formatNumber_whole(
                                                                users.length
                                                            )} ${
                                                                users.length === 1
                                                                    ? 'user'
                                                                    : 'users'
                                                            })`}
                                                        </Typography>
                                                        {isInternalUser() && (
                                                            <>
                                                                <Typography
                                                                    variant="h6"
                                                                    component="span"
                                                                >
                                                                    {' · '}
                                                                    {currentOrganization.id}
                                                                </Typography>
                                                                <ClickToCopyButton
                                                                    variant="icon"
                                                                    textToCopy={String(
                                                                        currentOrganization.id
                                                                    )}
                                                                    textToDisplay="Copy organization ID"
                                                                />
                                                            </>
                                                        )}
                                                    </Box>
                                                )}
                                                {/* admin_subOrgUser__edit — urban POST /users (userEditMw); sub-org user create (not admin_subOrg__edit). */}
                                                {currentOrganization !== null &&
                                                    currentOrganization !== 'all' &&
                                                    can(
                                                        PermissionsMapping.ADMIN_SUB_ORG_USER__EDIT
                                                    ) && (
                                                        <Button
                                                            color="primary"
                                                            variant="contained"
                                                            onClick={() =>
                                                                dispatch(
                                                                    adminUserFormActions.openForm(
                                                                        null,
                                                                        currentOrganization.id
                                                                    )
                                                                )
                                                            }
                                                        >
                                                            New User
                                                        </Button>
                                                    )}
                                            </Toolbar>
                                        </AppBar>
                                        <div className={classes.tableScroll}>
                                            {!isPageLoadingUi && (
                                                <>
                                                    <Table
                                                        size="small"
                                                        stickyHeader
                                                        className={classes.usersTable}
                                                    >
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Name</TableCell>
                                                                <TableCell>Email</TableCell>
                                                                <TableCell>Role</TableCell>
                                                                {isAllOrgsView && (
                                                                    <TableCell>
                                                                        Organization
                                                                    </TableCell>
                                                                )}
                                                                <TableCell>Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {filteredUsers.map(user => {
                                                                // EFB-1213: issue with Edit/Deprecate when user's org is not a direct child (urban user PATCH/DELETE PARENT|OWNER).
                                                                const userOrgMeta =
                                                                    currentOrganization !== null &&
                                                                    currentOrganization !== 'all' &&
                                                                    currentOrganization.id ===
                                                                        user.organization
                                                                        ? currentOrganization
                                                                        : organizationsForList.find(
                                                                              org =>
                                                                                  org.id ===
                                                                                  user.organization
                                                                          );
                                                                // Same gate as sidebar Edit/Deprecate — urban user PATCH/DELETE allow PARENT|OWNER only.
                                                                const isDirectChildOrg =
                                                                    userOrgMeta
                                                                        ?.relationshipToAuthOrganization
                                                                        ?._relationship === 'child';
                                                                const canEditOrDeleteUser = isDirectChildOrg;
                                                                const isSelfUser =
                                                                    user.id === currentUserId;

                                                                return (
                                                                    <TableRow key={user.id}>
                                                                        <TableCell>
                                                                            {user.name}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {user.email}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {rolePresentationDictionary[
                                                                                user.global_role
                                                                            ] || 'Unknown'}
                                                                        </TableCell>
                                                                        {isAllOrgsView && (
                                                                            <TableCell>
                                                                                {user.org.name}
                                                                            </TableCell>
                                                                        )}
                                                                        <TableCell>
                                                                            <Box
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    justifyContent:
                                                                                        'flex-start',
                                                                                }}
                                                                            >
                                                                                {isInternalUser() && (
                                                                                    <ClickToCopyButton
                                                                                        variant="icon"
                                                                                        textToCopy={String(
                                                                                            user.id
                                                                                        )}
                                                                                        textToDisplay="Copy user ID"
                                                                                    />
                                                                                )}
                                                                                {/* admin_subOrgUser__edit — urban PATCH /users/:id (userEditMw); sub-org user edit. */}
                                                                                {/* EFB-1213: issue with Edit when user's org is not a direct child (same as org Edit/Deprecate). */}
                                                                                {can(
                                                                                    PermissionsMapping.ADMIN_SUB_ORG_USER__EDIT
                                                                                ) && (
                                                                                    <Tooltip
                                                                                        title={
                                                                                            <Typography variant="body1">
                                                                                                {canEditOrDeleteUser
                                                                                                    ? 'Edit user'
                                                                                                    : 'You can only edit users in direct child organizations'}
                                                                                            </Typography>
                                                                                        }
                                                                                    >
                                                                                        {/* MUI Tooltip does not fire on disabled IconButton — span wrapper required. */}
                                                                                        <span>
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                aria-label="Edit user"
                                                                                                disabled={
                                                                                                    !canEditOrDeleteUser
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    dispatch(
                                                                                                        adminUserFormActions.openForm(
                                                                                                            user.id,
                                                                                                            user.organization
                                                                                                        )
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <EditOutlinedIcon />
                                                                                            </IconButton>
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                )}
                                                                                {/* admin_subOrgUser__edit — urban DELETE /users/:id (deleteUserMw); sub-org user delete. */}
                                                                                {/* EFB-1213: issue with Delete when user's org is not a direct child (same as org Edit/Deprecate). */}
                                                                                {can(
                                                                                    PermissionsMapping.ADMIN_SUB_ORG_USER__EDIT
                                                                                ) && (
                                                                                    <Tooltip
                                                                                        title={
                                                                                            <Typography variant="body1">
                                                                                                {isSelfUser
                                                                                                    ? 'You cannot delete your own account'
                                                                                                    : canEditOrDeleteUser
                                                                                                    ? 'Delete user'
                                                                                                    : 'You can only delete users in direct child organizations'}
                                                                                            </Typography>
                                                                                        }
                                                                                    >
                                                                                        {/* MUI Tooltip does not fire on disabled IconButton — span wrapper required. */}
                                                                                        <span>
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                aria-label="Delete user"
                                                                                                disabled={
                                                                                                    isSelfUser ||
                                                                                                    !canEditOrDeleteUser
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    dispatch(
                                                                                                        deleteModalActions.open(
                                                                                                            user
                                                                                                        )
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <DeleteOutlineIcon />
                                                                                            </IconButton>
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                )}
                                                                            </Box>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                    {filteredUsers.length === 0 && (
                                                        <Typography align="center" sx={{ pt: 2 }}>
                                                            No Users Found
                                                        </Typography>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </BlockLoadGroup>
            </div>
            <DeleteUserModal refreshUsers={refreshUsersFromServer} />
            <DeprecateOrgModal />
            <AdminUserForm refreshUsers={refreshUsersFromServer} />
            <AdminOrgForm refreshOrganization={refreshOrganization} />
        </div>
    );
};

export default connect(selector)(ChildOrganizationDashboard);
